import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Rss, ExternalLink, Users, BookOpen } from 'lucide-react';
import { publicApi } from '../lib/api';
import { useAuthStore } from '../store/useStore';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

interface PublicPost {
  id: string;
  title: string;
  summary: string | null;
  status: string;
  isPublic: boolean;
  publishedAt: string | null;
  createdAt: string;
  triggerArticle: {
    id: string;
    title: string;
    url: string;
    excerpt: string | null;
    content: string;
    imageUrl: string | null;
    publishedAt: string | null;
    feed: {
      id: string;
      title: string;
      siteUrl: string | null;
    };
  };
  authors: Array<{
    id: string;
    role: string;
    user: {
      id: string;
      username: string;
      displayName: string | null;
      avatar: string | null;
      bio: string | null;
    };
  }>;
  sections: Array<{
    id: string;
    content: string;
    order: number;
    author: {
      id: string;
      username: string;
      displayName: string | null;
      avatar: string | null;
    };
  }>;
}

interface InstanceInfo {
  name: string;
  description: string;
  allowRegistration: boolean;
  version: string;
  userCount: number;
}

export default function PublicPostDetail() {
  const { id } = useParams<{ id: string }>();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [post, setPost] = useState<PublicPost | null>(null);
  const [instanceInfo, setInstanceInfo] = useState<InstanceInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!id) return;

      try {
        const [postData, instance] = await Promise.all([
          publicApi.getPublicPost(id),
          publicApi.getInstance(),
        ]);
        setPost(postData);
        setInstanceInfo(instance);
      } catch (err) {
        setError('Post not found or not publicly available');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-ink-muted">Loading...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-canvas border-b border-edge">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Rss className="w-6 h-6 text-accent-600" />
              <span className="text-2xl font-bold">
                <span className="text-ink">ou</span>
                <span className="text-accent-600">r</span>
                <span className="text-ink">ss</span>
              </span>
            </Link>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <p className="text-red-500 mb-4">{error || 'Post not found'}</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-canvas border-b border-edge sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Rss className="w-6 h-6 text-accent-600" />
            <span className="text-2xl font-bold">
              <span className="text-ink">ou</span>
              <span className="text-accent-600">r</span>
              <span className="text-ink">ss</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link
                to="/friends"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                Open Reader
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-ink-secondary hover:text-gray-900 font-medium"
                >
                  Sign In
                </Link>
                {instanceInfo?.allowRegistration && (
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                  >
                    Join
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-ink-muted hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <article className="bg-canvas rounded-lg shadow-sm overflow-hidden">
          {/* Post Header */}
          <div className="p-6 border-b border-edge">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm px-3 py-1 rounded font-medium bg-green-100 text-green-700">
                Published
              </span>
              <span className="text-sm px-3 py-1 rounded font-medium bg-blue-100 text-blue-700">
                Public
              </span>
              <span className="text-xs px-2 py-1 bg-gray-100 text-ink-muted rounded">
                Collaborative Post
              </span>
            </div>

            <h1 className="text-3xl font-bold text-ink mb-3">{post.title}</h1>

            {post.summary && (
              <p className="text-lg text-ink-muted mb-4">{post.summary}</p>
            )}

            {post.publishedAt && (
              <p className="text-sm text-ink-muted mb-4">
                Published {formatDistanceToNow(new Date(post.publishedAt), { addSuffix: true })}
              </p>
            )}

            {/* Trigger Article */}
            <div className="bg-gray-50 border border-edge rounded-lg p-4 mb-6">
              <div className="text-sm text-ink-muted mb-1">In response to:</div>
              <div className="font-medium text-ink mb-2">{post.triggerArticle.title}</div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-accent-600">{post.triggerArticle.feed.title}</span>
                <a
                  href={post.triggerArticle.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-ink-muted hover:text-accent-600"
                >
                  <ExternalLink className="w-3 h-3" />
                  View original
                </a>
              </div>
            </div>

            {/* Contributors */}
            <div className="flex items-center gap-2 flex-wrap">
              <Users className="w-4 h-4 text-ink-muted" />
              <span className="text-sm text-ink-muted mr-2">Contributors:</span>
              {post.authors.map((author) => (
                <div
                  key={author.id}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full"
                >
                  {author.user.avatar ? (
                    <img
                      src={author.user.avatar}
                      alt=""
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-semibold">
                      {author.user.username[0].toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm font-medium text-ink-secondary">
                    {author.user.displayName || author.user.username}
                  </span>
                  {author.role === 'initiator' && (
                    <span className="text-xs text-ink-muted">(started)</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="p-6">
            {post.sections.length === 0 ? (
              <div className="text-center py-12 text-ink-muted">
                No content available.
              </div>
            ) : (
              <div className="space-y-8">
                {post.sections.map((section, index) => (
                  <div key={section.id} className="border-b border-gray-100 pb-8 last:border-0">
                    {/* Section Author */}
                    <div className="flex items-center gap-3 mb-4">
                      {section.author.avatar ? (
                        <img
                          src={section.author.avatar}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                          {section.author.username[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-ink">
                          {section.author.displayName || section.author.username}
                        </div>
                        <div className="text-xs text-ink-muted">
                          Section {index + 1} of {post.sections.length}
                        </div>
                      </div>
                    </div>

                    {/* Section Content */}
                    <div className="prose prose-slate max-w-none">
                      <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        components={{
                          p: ({ children }) => <p className="mb-4 text-ink-secondary leading-relaxed">{children}</p>,
                          h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 mt-6 text-ink">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-xl font-bold mb-3 mt-5 text-ink">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-lg font-bold mb-2 mt-4 text-ink">{children}</h3>,
                          ul: ({ children }) => <ul className="list-disc ml-6 mb-4 text-ink-secondary">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal ml-6 mb-4 text-ink-secondary">{children}</ol>,
                          li: ({ children }) => <li className="mb-1">{children}</li>,
                          a: ({ children, href }) => (
                            <a
                              href={href}
                              className="text-accent-600 hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          ),
                          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                          code: ({ children }) => (
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">
                              {children}
                            </code>
                          ),
                          pre: ({ children }) => (
                            <pre className="bg-gray-100 p-4 rounded mb-4 overflow-x-auto">
                              {children}
                            </pre>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-gray-300 pl-4 italic text-ink-muted my-4">
                              {children}
                            </blockquote>
                          ),
                        }}
                      >
                        {section.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </article>

        {/* Join CTA - only show for non-authenticated users */}
        {!isAuthenticated && (
          <div className="mt-8 p-6 bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg text-center">
            <h4 className="font-semibold text-ink mb-2">Want to join the conversation?</h4>
            <p className="text-sm text-ink-muted mb-4">
              Create an account to participate in collaborative posts and share your own articles.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link
                to="/login"
                className="px-6 py-2 border border-gray-300 text-ink-secondary rounded-lg hover:bg-white font-medium"
              >
                Sign In
              </Link>
              {instanceInfo?.allowRegistration && (
                <Link
                  to="/register"
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                >
                  Create Account
                </Link>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-canvas border-t border-edge py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-ink-muted">
          <div className="flex items-center justify-center gap-4 mb-4">
            <a
              href="/feed.xml"
              className="flex items-center gap-1 hover:text-accent-600"
            >
              <Rss className="w-4 h-4" />
              RSS Feed
            </a>
          </div>
          <p className="text-sm">
            Powered by OurSS - A self-hosted RSS reader with social features
          </p>
        </div>
      </footer>
    </div>
  );
}
